import { CurrencyPipe, getCurrencySymbol as AngularGetCurrencySymbol } from '@angular/common';
import { Injectable, Inject } from '@angular/core';
import { LocalizationStrategy, Localized, Region, mapLocaleToTransKey } from '../localization';
import { LOCALIZATION_STRATEGY } from '../tokens';
import { LocaleSettings } from '../locale-settings';
import { Locale } from '../locale';

@Injectable()
export class LocaleService implements Localized {
  private localeSettings: LocaleSettings;
  private currencyFormatter: CurrencyPipe;

  constructor(
    @Inject(LOCALIZATION_STRATEGY) private localizationStrategy: LocalizationStrategy,
    @Inject('LOCALE_TRANSLATION') private localeTranslation: string
  ) {
    this.localeSettings = this.localizationStrategy.localize();
    this.currencyFormatter = new CurrencyPipe(this.getLocale().toString());
    this.localeTranslation = JSON.parse(localeTranslation);
  }

  /**
   * Returns the translation value.
   *
   * The translation value is the one that is set on phraseapp
   *
   * @returns translated message
   */
  getTranslationValue(prop: string): string {
    return this.localeTranslation[prop] ? this.localeTranslation[prop].message : '';
  }

  /**
   * Returns the translation key.
   *
   * The translation key identifies the locale to be used for translating.
   * This translation key represents the old locale (language code)
   * as defined on legacy website.
   *
   * @returns 2-letter translation key
   */
  getTranslationKey(): string {
    return mapLocaleToTransKey(this.getLocale());
  }

  /**
   * Returns the current site.
   *
   * @deprecated prefer using {LocaleService.getRegion()} instead
   * @returns the site Id
   */
  getSiteId(): string {
    return this.getRegion().toString();
  }

  /**
   * @inheritDoc
   */
  getLocale(): Locale {
    return this.localeSettings.getLocale();
  }

  /**
   * @inheritDoc
   */
  getLanguageTag(): string {
    return `${this.localeSettings.getLanguage()}-${this.localeSettings.getLocaleRegion()}`;
  }

  /**
   * @inheritDoc
   */
  getRegion(): Region {
    return this.localeSettings.getRegion();
  }

  /**
   * @inheritDoc
   */
  getRegionCode(): string {
    return this.localeSettings.getRegionCode();
  }

  /**
   * @inheritDoc
   */
  getCurrency(): string {
    return this.localeSettings.getCurrency();
  }

  /**
   * @inheritDoc
   */
  getCurrencySymbol(currency: string | null): string {
    if (!currency) {
      currency = this.getCurrency();
    }
    return AngularGetCurrencySymbol(currency, 'narrow', this.getLanguage());
  }

  /**
   * @inheritDoc
   */
  getCountry(): string {
    return this.localeSettings.getCountry();
  }

  /**
   * @inheritDoc
   */
  getLanguage(): string {
    return this.getLocale().getLanguage();
  }

  /**
   * Returns whether current locale settings imply to explicitly display
   * prices including all taxes and additional charges.
   */
  shouldDisplayTaxes(): boolean {
    return this.getRegion() === Region.DE;
  }

  /**
   * Format a number as currency.
   *
   * @param value the number to be formatted
   * @param currency the 3-letter currency code (uses the current currency by default)
   *
   * @returns the formatted currency
   */
  formatCurrency(value: number, currency?: string): string {
    const currentCurrency = currency || this.getCurrency();
    const defaultCurrencyOptions = this.getDefaultCurrencyOptions(currentCurrency);
    return this.currencyFormatter.transform(
      value,
      currentCurrency,
      defaultCurrencyOptions.symbol,
      '1.0-2',
      defaultCurrencyOptions.local
    );
  }

  /**
   * TEMPORARY
   * Check if size are available for i18n country iso code
   *
   * @param countryCodeIso the isoCode of i18n country
   *
   * @returns the available iso code for the size on filter
   */
  getIsoCodeForSize(countryCodeIso: string): string {
    const isoSizeAvailable = ['IT', 'FR', 'US', 'ES', 'DE', 'UK'];
    if (countryCodeIso === 'GB') {
      return 'UK';
    }
    if (isoSizeAvailable.indexOf(countryCodeIso) === -1) {
      return 'US';
    }
    return countryCodeIso;
  }

  getDefaultCurrencyOptions(currency: string) {
    const defaultCurrenciesOptions = {
      USD: { local: 'en-US', symbol: 'symbol' },
      GBP: { local: 'en-GB', symbol: 'symbol' },
      HKD: { local: 'en-GB', symbol: 'symbol' },
      SGD: { local: 'en-GB', symbol: 'S$' },
      AUD: { local: 'en-GB', symbol: 'code' },
      CAD: { local: 'en-US', symbol: 'CDN$' },
      PLN: { local: 'fr', symbol: 'symbol-narrow' },
      CHF: { local: 'fr', symbol: 'symbol' },
      SEK: { local: 'fr', symbol: 'symbol' },
      DKK: { local: 'fr', symbol: 'symbol' },
      JPY: { local: 'en-US', symbol: 'symbol-narrow' },
      CNY: { local: 'en-US', symbol: 'symbol' },
      EUR: { local: 'fr', symbol: 'symbol' }
    };

    const defaultOptions = { local: 'en-US', symbol: 'symbol' };
    return defaultCurrenciesOptions[currency] || defaultOptions;
  }
}
